import { type Channel } from 'redux-saga';
import {
  actionChannel, call,
  put, take, takeLatest,
} from 'redux-saga/effects';
import type { GroupingColumn } from '~/_shared/types/grouping/grouping';
import { select } from '~/_shared/utils/saga/effects';
import type { ActiveProximity } from '~/sidebar/sidebarApps/rightSidebar/rightSidebarConfiguration';
import type {
  BoundaryTerritoryIdentifier, ExtendedBoundaryIdentifier,
} from '~/store/boundaries/boundaryIdentifier.type';
import { boundaryTerritoryGroupActivate } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionTypes';
import { hasBoundaryTerritoryGroupLocationData } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.helpers';
import { boundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  activeMapElementsSetPlace,
  clearSpreadsheetPreviewFilterState,
  clickSpreadsheetMarker, deactivateActiveBoundary, deactivateActiveMarker, deactivateActiveProximity,
  setActiveOverlappingElementsBoundary, setActiveOverlappingElementsProximity, setActiveOverlappingLocation,
  setSpreadsheetPreviewFilterState,
} from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import {
  ACTIVE_MAP_ELEMENTS_CLEAR_STATE, ACTIVE_MAP_ELEMENTS_DEACTIVATE_ACTIVE_MARKER, ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY, ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY_TERRITORY, ACTIVE_MAP_ELEMENTS_SET_ACTIVE_PROXIMITY, ACTIVE_MAP_SET_ACTIVE_LOCATION_LIST, ACTIVE_MAP_SET_ACTIVE_MASTER_KEY, SPREADSHEET_MARKER_CLICKED,
} from '~/store/frontendState/activeMapElements/activeMapElements.actionTypes';
import {
  activeBoundarySelector, activeBoundaryTerritorySelector, activeMarkerIdsSelector, activeOverlappingLocationSelector, activeProximitySelector,
} from '~/store/frontendState/activeMapElements/activeMapElements.selectors';
import { MAP_PAGE_INIT } from '~/store/map/map.actionTypes';
import { getGroupingFiltersToCalculate } from '~/store/mapSettings/columnsFilter/mapSettingsColumnsFilter.sagas';
import { MAP_SETTINGS_SEARCH_ITEMS_ADD_ITEM } from '~/store/mapSettings/searchItems/mapSettingsSearchItems.actionTypes';
import { MAP_SETTINGS_TOOLS_STATE_BOUNDARY_SET_LOCATIONS_FILTER } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.actionTypes';
import { MAP_SETTINGS_HEATMAP_UPDATE_TOOLSTATE_DATA } from '~/store/mapSettings/toolsState/heatmaps/heatmapToolState.actionTypes';
import { setLocationFinderToolsState } from '~/store/mapSettings/toolsState/locationFinder/locationFinder.actionCreators';
import { selectFilteredLatLngSpreadsheetData } from '~/store/selectors/spreadsheetDataMemoizedSelectors';
import {
  SPREADSHEET_ADD_FILTER_ITEM, SPREADSHEET_FETCH_DATA_SUCCESS,
} from '~/store/spreadsheetData/spreadsheetData.actionTypes';
import { spreadsheetDataStateSelector } from '~/store/spreadsheetData/spreadsheetData.selectors';
import { currentClientCurrentMapSplitItemSettingsSelector } from '~/store/userData/settings/userDataSettings.selectors';
import { FRONTEND_STATE_DRAWING_EDIT_SET_SELECTED_DRAWING } from '../mapTools/drawing/drawingEdit/drawingEdit.actionTypes';
import {
  weightedCenterClearSelectedEntities, weightedCenterSetSelectedEntities,
} from '../mapTools/weightedCenter/weightedCenter.actionCreators';
import { isWeightedCenterActiveSelector } from '../mapTools/weightedCenter/weightedCenter.selectors';

export const EMPTY_OVERLAP = [];

export function* activeMapElementsSagas() {
  yield takeLatest([
    MAP_SETTINGS_HEATMAP_UPDATE_TOOLSTATE_DATA,
    MAP_SETTINGS_SEARCH_ITEMS_ADD_ITEM,
    MAP_SETTINGS_TOOLS_STATE_BOUNDARY_SET_LOCATIONS_FILTER,
    SPREADSHEET_ADD_FILTER_ITEM,
    SPREADSHEET_FETCH_DATA_SUCCESS,
  ], onFilter);
  yield takeLatest([
    deactivateActiveProximity,
    deactivateActiveBoundary,
    setActiveOverlappingLocation,
    ACTIVE_MAP_ELEMENTS_SET_ACTIVE_PROXIMITY,
  ], overlappingElementsConsistencySaga);
  yield watchActiveEntityChanges();
}

const activeEntityChangeActions = [
  setLocationFinderToolsState,
  deactivateActiveMarker,
  deactivateActiveProximity,
  deactivateActiveBoundary,
  boundaryTerritoryGroupActivate,
  activeMapElementsSetPlace,
  SPREADSHEET_MARKER_CLICKED,
  MAP_PAGE_INIT,
  ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY,
  ACTIVE_MAP_ELEMENTS_SET_ACTIVE_BOUNDARY_TERRITORY,
  BOUNDARY_TERRITORY_GROUP_REMOVE_COMPLETED,
  FRONTEND_STATE_DRAWING_EDIT_SET_SELECTED_DRAWING,
  ACTIVE_MAP_ELEMENTS_CLEAR_STATE,
  ACTIVE_MAP_ELEMENTS_DEACTIVATE_ACTIVE_MARKER,
  ACTIVE_MAP_ELEMENTS_SET_ACTIVE_PROXIMITY,
  ACTIVE_MAP_SET_ACTIVE_LOCATION_LIST,
  ACTIVE_MAP_SET_ACTIVE_MASTER_KEY,
];

function* onFilter() {
  try {
    const filteredRowIds: ReturnType<typeof selectFilteredLatLngSpreadsheetData> = yield select(selectFilteredLatLngSpreadsheetData);
    const activeMarkerIds: ReturnType<typeof activeMarkerIdsSelector> = yield select(activeMarkerIdsSelector);
    const spreadsheetDataState: ReturnType<typeof spreadsheetDataStateSelector> = yield select(spreadsheetDataStateSelector);
    const remainingGroupingFiltersToCalculate: GroupingColumn[] = yield call(getGroupingFiltersToCalculate);

    // if remainingGroupingFiltersToCalculate is more than 0, it means some group filters are still calculated so we cannot procced with the process below
    // after they are done, SPREADSHEET_ADD_FILTER_ITEM will be dispatched and this saga will be called again
    if (!activeMarkerIds || spreadsheetDataState.isLoading || remainingGroupingFiltersToCalculate.length > 0) {
      return;
    }

    const filteredMarkers = activeMarkerIds.filter(markerSpreadsheetRowId =>
      filteredRowIds.visible.getRow(markerSpreadsheetRowId)
    );

    if (filteredMarkers.length === activeMarkerIds.length) {
      return;
    }

    if (filteredMarkers.length === 0) {
      yield put(deactivateActiveMarker());
    }
    else {
      yield put(clickSpreadsheetMarker({ rowIds: filteredMarkers }));
    }
  }
  catch (error) {
    console.error('Error in onFilter saga:', error);
  }
}

function* overlappingElementsConsistencySaga() {
  const activeProximity: ReturnType<typeof activeProximitySelector> = yield select(activeProximitySelector);
  const overlapLoc: ReturnType<typeof activeOverlappingLocationSelector> = yield select(activeOverlappingLocationSelector);
  const activeBoundary: ReturnType<typeof activeBoundarySelector> = yield select(activeBoundarySelector);

  if (!overlapLoc || !activeBoundary) {
    yield put(setActiveOverlappingElementsBoundary(EMPTY_OVERLAP));
  }

  if (!overlapLoc || !activeProximity) {
    yield put(setActiveOverlappingElementsProximity(EMPTY_OVERLAP));
  }
}

function* watchActiveEntityChanges() {
  const activeEntityChangeChannel: Channel<Action> = yield actionChannel(activeEntityChangeActions);

  let previousActiveBoundary: ExtendedBoundaryIdentifier | null = null;
  let previousActiveBoundaryTerritory: BoundaryTerritoryIdentifier | null = null;
  let previousActiveProximity: ActiveProximity | null = null;

  while (true) {
    yield take(activeEntityChangeChannel);

    const activeProximity: ReturnType<typeof activeProximitySelector> = yield select(activeProximitySelector);
    const activeBoundary: ReturnType<typeof activeBoundarySelector> = yield select(activeBoundarySelector);
    const activeBoundaryTerritory: ReturnType<typeof activeBoundaryTerritorySelector> = yield select(activeBoundaryTerritorySelector);

    const currentClientCurrentMapSplitItemSettings: ReturnType<typeof currentClientCurrentMapSplitItemSettingsSelector> = yield select(currentClientCurrentMapSplitItemSettingsSelector);
    const isWeightedCenterActive: ReturnType<typeof isWeightedCenterActiveSelector> = yield select(isWeightedCenterActiveSelector);
    const boundaryTerritoryGroups: ReturnType<typeof boundaryTerritoryGroupsSelector> = yield select(boundaryTerritoryGroupsSelector);

    const isLocationDataAvailable = (boundaryTerritoryGroupId: number) =>
      hasBoundaryTerritoryGroupLocationData(
        boundaryTerritoryGroups.find(group => group.boundaryTerritoryGroupId === boundaryTerritoryGroupId)
      );

    const isAnyEntityActive = activeProximity !== null || activeBoundary !== null || activeBoundaryTerritory !== null;
    const wasAnyEntityActive = previousActiveProximity !== null || previousActiveBoundary !== null || previousActiveBoundaryTerritory !== null;

    // if split screen panel is open, we want to chnage spreadsheet preview filters
    if (currentClientCurrentMapSplitItemSettings.isActive) {
      if (activeProximity && activeProximity !== previousActiveProximity) {
        yield put(setSpreadsheetPreviewFilterState({
          proximities: [activeProximity],
        }));
      }

      if (activeBoundary && activeBoundary !== previousActiveBoundary && isLocationDataAvailable(activeBoundary.boundaryTerritoryGroupId)) {
        yield put(setSpreadsheetPreviewFilterState({
          boundaries: [activeBoundary],
        }));
      }

      if (activeBoundaryTerritory && activeBoundaryTerritory !== previousActiveBoundaryTerritory && isLocationDataAvailable(activeBoundaryTerritory.boundaryTerritoryGroupId)) {
        yield put(setSpreadsheetPreviewFilterState({
          boundaryTerritories: [activeBoundaryTerritory],
        }));
      }

      if (!isAnyEntityActive && wasAnyEntityActive) {
        yield put(clearSpreadsheetPreviewFilterState());
      }
    }

    // if weighted center is active, we want to change weighted center selected entities
    if (isWeightedCenterActive) {
      if (activeProximity && activeProximity !== previousActiveProximity) {
        yield put(weightedCenterSetSelectedEntities({
          proximities: [activeProximity],
        }));
      }

      if (activeBoundary && activeBoundary !== previousActiveBoundary) {
        yield put(weightedCenterSetSelectedEntities({
          boundaries: [activeBoundary],
        }));
      }

      if (activeBoundaryTerritory && activeBoundaryTerritory !== previousActiveBoundaryTerritory) {
        yield put(weightedCenterSetSelectedEntities({
          boundaryTerritories: [activeBoundaryTerritory],
        }));
      }

      if (!isAnyEntityActive && wasAnyEntityActive) {
        yield put(weightedCenterClearSelectedEntities());
      }
    }

    previousActiveBoundary = activeBoundary;
    previousActiveBoundaryTerritory = activeBoundaryTerritory;
    previousActiveProximity = activeProximity;
  }
}
